import { useEffect, useMemo, useState } from 'react';
import type { CellContextMenuEvent, ColDef, RowDoubleClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import {
  Boxes,
  Building2,
  ClipboardList,
  Download,
  FileText,
  HardDrive,
  KeyRound,
  Search,
  Truck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { getApiErrorMessage } from '@/api/apiErrors';
import type { Environment, Tenant, TenantStatistics } from '@/api/apiTypes';
import { AppDataGrid } from '@/components/grid/AppDataGrid';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useTenants } from '@/hooks/useTenants';
import { useEnvironmentScope } from '@/providers/useEnvironmentScope';
import { exportCsv } from '@/utils/csv';

const EMPTY_TENANTS: Tenant[] = [];
const EMPTY_ENVIRONMENTS: Environment[] = [];

type TenantMetricSummary = {
  totalAssets: number;
  totalLicenses: number;
  totalContracts: number;
  totalSuppliers: number;
  totalCustomers: number;
  totalWorkOrders: number;
  totalUsers: number;
  totalStorageBytes: number;
};

const EMPTY_SUMMARY: TenantMetricSummary = {
  totalAssets: 0,
  totalLicenses: 0,
  totalContracts: 0,
  totalSuppliers: 0,
  totalCustomers: 0,
  totalWorkOrders: 0,
  totalUsers: 0,
  totalStorageBytes: 0,
};

const INTEGER_FORMATTER = new Intl.NumberFormat('en-US');

type TenantContextMenuState = {
  tenant: Tenant;
  x: number;
  y: number;
};

function formatInteger(value: number) {
  return INTEGER_FORMATTER.format(value);
}

function formatIntegerCell(params: ValueFormatterParams<Tenant>) {
  return formatInteger(Number(params.value ?? 0));
}

function formatStorage(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = -1;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

function getTenantStatistics(statistics: TenantStatistics | null) {
  return {
    total_assets: statistics?.total_assets ?? 0,
    total_licenses: statistics?.total_licenses ?? 0,
    total_contracts: statistics?.total_contracts ?? 0,
    total_suppliers: statistics?.total_suppliers ?? 0,
    total_customers: statistics?.total_customers ?? 0,
    total_workorders: statistics?.total_workorders ?? 0,
    total_users: statistics?.total_users ?? 0,
    storage_bytes: statistics?.storage_bytes ?? 0,
  };
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [contextMenu, setContextMenu] = useState<TenantContextMenuState | null>(null);
  const { environmentScope, selectedEnvironmentId } = useEnvironmentScope();

  const environmentsQuery = useEnvironments();
  const environments = environmentsQuery.data ?? EMPTY_ENVIRONMENTS;
  const tenantsQuery = useTenants(selectedEnvironmentId);
  const dashboardStatsQuery = useDashboardStats(environmentScope === 'all');
  const scopedTenants = tenantsQuery.data ?? EMPTY_TENANTS;

  const tenantSummary = useMemo(() => {
    return scopedTenants.reduce<TenantMetricSummary>((currentSummary, tenant) => {
      const statistics = getTenantStatistics(tenant.statistics);

      return {
        totalAssets: currentSummary.totalAssets + statistics.total_assets,
        totalLicenses: currentSummary.totalLicenses + statistics.total_licenses,
        totalContracts: currentSummary.totalContracts + statistics.total_contracts,
        totalSuppliers: currentSummary.totalSuppliers + statistics.total_suppliers,
        totalCustomers: currentSummary.totalCustomers + statistics.total_customers,
        totalWorkOrders: currentSummary.totalWorkOrders + statistics.total_workorders,
        totalUsers: currentSummary.totalUsers + statistics.total_users,
        totalStorageBytes: currentSummary.totalStorageBytes + statistics.storage_bytes,
      };
    }, EMPTY_SUMMARY);
  }, [scopedTenants]);

  const summary = useMemo<TenantMetricSummary>(() => {
    if (environmentScope !== 'all' || !dashboardStatsQuery.data) {
      return tenantSummary;
    }

    return {
      totalAssets: dashboardStatsQuery.data.total_assets,
      totalLicenses: dashboardStatsQuery.data.total_licenses,
      totalContracts: dashboardStatsQuery.data.total_contracts,
      totalSuppliers: dashboardStatsQuery.data.total_suppliers,
      totalCustomers: dashboardStatsQuery.data.total_customers,
      totalWorkOrders: dashboardStatsQuery.data.total_workorders,
      totalUsers: dashboardStatsQuery.data.total_users,
      totalStorageBytes: dashboardStatsQuery.data.storage_bytes,
    };
  }, [dashboardStatsQuery.data, environmentScope, tenantSummary]);

  const visibleTenants = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredTenants = scopedTenants.filter((tenant) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        tenant.name.toLowerCase().includes(normalizedSearch) ||
        tenant.schema_name.toLowerCase().includes(normalizedSearch);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && tenant.is_active) ||
        (statusFilter === 'inactive' && !tenant.is_active);

      return matchesSearch && matchesStatus;
    });

    return filteredTenants.toSorted((leftTenant, rightTenant) => leftTenant.name.localeCompare(rightTenant.name));
  }, [scopedTenants, searchTerm, statusFilter]);

  const summaryCards = [
    {
      label: t('dashboard.metrics.tenants'),
      value: formatInteger(environmentScope === 'all' && dashboardStatsQuery.data
        ? dashboardStatsQuery.data.total_tenants
        : scopedTenants.length),
      icon: Building2,
      tone: 'blue',
    },
    {
      label: t('dashboard.metrics.assets'),
      value: formatInteger(summary.totalAssets),
      icon: Boxes,
      tone: 'green',
    },
    {
      label: t('dashboard.metrics.contracts'),
      value: formatInteger(summary.totalContracts),
      icon: FileText,
      tone: 'violet',
    },
    {
      label: t('dashboard.metrics.suppliers'),
      value: formatInteger(summary.totalSuppliers),
      icon: Truck,
      tone: 'amber',
    },
    {
      label: t('dashboard.metrics.customers'),
      value: formatInteger(summary.totalCustomers),
      icon: Users,
      tone: 'rose',
    },
    {
      label: t('dashboard.metrics.licenses'),
      value: formatInteger(summary.totalLicenses),
      icon: KeyRound,
      tone: 'blue',
    },
    {
      label: t('dashboard.metrics.workOrders'),
      value: formatInteger(summary.totalWorkOrders),
      icon: ClipboardList,
      tone: 'violet',
    },
    {
      label: t('dashboard.metrics.storage'),
      value: formatStorage(summary.totalStorageBytes),
      icon: HardDrive,
      tone: 'green',
    },
  ];

  const selectedEnvironmentName =
    environmentScope === 'all'
      ? t('dashboard.scope.allAssigned')
      : environments.find((environment) => environment.id === selectedEnvironmentId)?.name ?? t('dashboard.scope.unknown');

  useEffect(() => {
    if (!contextMenu) {
      return undefined;
    }

    const closeMenu = () => setContextMenu(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  const openTenantDetails = (tenant: Tenant) => {
    navigate(`/tenants/${tenant.id}`);
  };

  const openTenantDetailsNewTab = (tenant: Tenant) => {
    window.open(`/tenants/${tenant.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleTenantRowDoubleClick = (event: RowDoubleClickedEvent<Tenant>) => {
    if (!event.data) {
      return;
    }

    openTenantDetails(event.data);
  };

  const handleTenantContextMenu = (event: CellContextMenuEvent<Tenant>) => {
    if (!event.data || !event.event || !(event.event instanceof MouseEvent)) {
      return;
    }

    event.event.preventDefault();
    setContextMenu({
      tenant: event.data,
      x: event.event.clientX,
      y: event.event.clientY,
    });
  };

  const tenantColumnDefs = useMemo<ColDef<Tenant>[]>(
    () => [
      {
        headerName: t('dashboard.table.columns.name'),
        field: 'name',
        minWidth: 250,
        cellRenderer: ({ data }: { data?: Tenant }) => {
          if (!data) {
            return null;
          }

          return (
            <Link className="tenant-cell" to={`/tenants/${data.id}`}>
              <span className="tenant-cell__icon">{data.name.slice(0, 2).toUpperCase()}</span>
              <span className="tenant-cell__content">
                <span className="tenant-cell__title">{data.name}</span>
                <span className="tenant-cell__meta">{data.schema_name}</span>
              </span>
            </Link>
          );
        },
      },
      {
        headerName: t('dashboard.table.columns.assets'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_assets,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 120,
        cellClass: 'grid-number grid-number--green',
      },
      {
        headerName: t('dashboard.table.columns.contracts'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_contracts,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 125,
        cellClass: 'grid-number grid-number--violet',
      },
      {
        headerName: t('dashboard.table.columns.suppliers'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_suppliers,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 125,
        cellClass: 'grid-number grid-number--amber',
      },
      {
        headerName: t('dashboard.table.columns.customers'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_customers,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 130,
        cellClass: 'grid-number grid-number--rose',
      },
      {
        headerName: t('dashboard.table.columns.licenses'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_licenses,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 120,
        cellClass: 'grid-number grid-number--blue',
      },
      {
        headerName: t('dashboard.table.columns.workOrders'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_workorders,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 135,
      },
      {
        headerName: t('dashboard.table.columns.users'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).total_users,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatIntegerCell,
        width: 110,
      },
      {
        headerName: t('dashboard.table.columns.storage'),
        valueGetter: ({ data }) => getTenantStatistics(data?.statistics ?? null).storage_bytes,
        valueFormatter: ({ value }) => formatStorage(Number(value ?? 0)),
        width: 130,
      },
      {
        headerName: t('dashboard.table.columns.environment'),
        valueGetter: ({ data }) => data?.environment.name ?? '',
        minWidth: 150,
        cellRenderer: ({ value }: { value?: string }) =>
          value ? <span className="environment-pill">{value}</span> : null,
      },
      {
        headerName: t('dashboard.table.columns.status'),
        valueGetter: ({ data }) => (data?.is_active ? t('dashboard.status.active') : t('dashboard.status.inactive')),
        width: 120,
        cellRenderer: ({ data }: { data?: Tenant }) => (
          <span className={`status-pill ${data?.is_active ? 'status-pill--active' : 'status-pill--inactive'}`}>
            {data?.is_active ? t('dashboard.status.active') : t('dashboard.status.inactive')}
          </span>
        ),
      },
    ],
    [t],
  );

  const handleExportTenants = () => {
    exportCsv('tenant-details.csv', [
      { key: 'environmentName', label: t('dashboard.export.environmentName'), resolve: (tenant) => tenant.environment.name },
      { key: 'environmentId', label: t('dashboard.export.environmentId'), resolve: (tenant) => tenant.environment.id },
      { key: 'tenantId', label: t('dashboard.table.columns.tenantId'), resolve: (tenant) => tenant.id },
      { key: 'subscriptionId', label: t('dashboard.table.columns.subscriptionId'), resolve: (tenant) => tenant.schema_name },
      { key: 'timestamp', label: t('dashboard.export.timestamp'), resolve: () => new Date().toISOString() },
      { key: 'name', label: t('dashboard.table.columns.name'), resolve: (tenant) => tenant.name },
      { key: 'status', label: t('dashboard.table.columns.status'), resolve: (tenant) => (tenant.is_active ? t('dashboard.status.active') : t('dashboard.status.inactive')) },
      { key: 'assets', label: t('dashboard.metrics.assets'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_assets },
      { key: 'contracts', label: t('dashboard.metrics.contracts'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_contracts },
      { key: 'suppliers', label: t('dashboard.metrics.suppliers'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_suppliers },
      { key: 'customers', label: t('dashboard.metrics.customers'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_customers },
      { key: 'licenses', label: t('dashboard.metrics.licenses'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_licenses },
      { key: 'workOrders', label: t('dashboard.metrics.workOrders'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_workorders },
      { key: 'users', label: t('dashboard.metrics.users'), resolve: (tenant) => getTenantStatistics(tenant.statistics).total_users },
      { key: 'storage', label: t('dashboard.metrics.storage'), resolve: (tenant) => getTenantStatistics(tenant.statistics).storage_bytes },
    ], visibleTenants);
  };

  const handleExportMetrics = () => {
    const exportTimestamp = new Date().toISOString();

    if (environmentScope === 'all') {
      const rows = environments.map((environment) => {
        const environmentTenants = scopedTenants.filter((tenant) => tenant.environment.id === environment.id);
        const environmentSummary = environmentTenants.reduce<TenantMetricSummary>((currentSummary, tenant) => {
          const statistics = getTenantStatistics(tenant.statistics);

          return {
            totalAssets: currentSummary.totalAssets + statistics.total_assets,
            totalLicenses: currentSummary.totalLicenses + statistics.total_licenses,
            totalContracts: currentSummary.totalContracts + statistics.total_contracts,
            totalSuppliers: currentSummary.totalSuppliers + statistics.total_suppliers,
            totalCustomers: currentSummary.totalCustomers + statistics.total_customers,
            totalWorkOrders: currentSummary.totalWorkOrders + statistics.total_workorders,
            totalUsers: currentSummary.totalUsers + statistics.total_users,
            totalStorageBytes: currentSummary.totalStorageBytes + statistics.storage_bytes,
          };
        }, EMPTY_SUMMARY);

        return {
          environmentId: environment.id,
          environmentName: environment.name,
          tenantCount: environmentTenants.length,
          ...environmentSummary,
          exportTimestamp,
        };
      });

      exportCsv('environment-summary.csv', [
        { key: 'environmentName', label: t('dashboard.export.environmentName'), resolve: (row) => row.environmentName },
        { key: 'environmentId', label: t('dashboard.export.environmentId'), resolve: (row) => row.environmentId },
        { key: 'tenantCount', label: t('dashboard.metrics.tenants'), resolve: (row) => row.tenantCount },
        { key: 'assets', label: t('dashboard.metrics.assets'), resolve: (row) => row.totalAssets },
        { key: 'licenses', label: t('dashboard.metrics.licenses'), resolve: (row) => row.totalLicenses },
        { key: 'contracts', label: t('dashboard.metrics.contracts'), resolve: (row) => row.totalContracts },
        { key: 'suppliers', label: t('dashboard.metrics.suppliers'), resolve: (row) => row.totalSuppliers },
        { key: 'customers', label: t('dashboard.metrics.customers'), resolve: (row) => row.totalCustomers },
        { key: 'workOrders', label: t('dashboard.metrics.workOrders'), resolve: (row) => row.totalWorkOrders },
        { key: 'users', label: t('dashboard.metrics.users'), resolve: (row) => row.totalUsers },
        { key: 'storage', label: t('dashboard.metrics.storage'), resolve: (row) => row.totalStorageBytes },
        { key: 'timestamp', label: t('dashboard.export.timestamp'), resolve: (row) => row.exportTimestamp },
      ], rows);

      return;
    }

    exportCsv('environment-summary.csv', [
      { key: 'environmentName', label: t('dashboard.export.environmentName'), resolve: () => selectedEnvironmentName },
      { key: 'environmentId', label: t('dashboard.export.environmentId'), resolve: () => selectedEnvironmentId ?? '' },
      { key: 'tenantCount', label: t('dashboard.metrics.tenants'), resolve: () => scopedTenants.length },
      { key: 'assets', label: t('dashboard.metrics.assets'), resolve: () => tenantSummary.totalAssets },
      { key: 'licenses', label: t('dashboard.metrics.licenses'), resolve: () => tenantSummary.totalLicenses },
      { key: 'contracts', label: t('dashboard.metrics.contracts'), resolve: () => tenantSummary.totalContracts },
      { key: 'suppliers', label: t('dashboard.metrics.suppliers'), resolve: () => tenantSummary.totalSuppliers },
      { key: 'customers', label: t('dashboard.metrics.customers'), resolve: () => tenantSummary.totalCustomers },
      { key: 'workOrders', label: t('dashboard.metrics.workOrders'), resolve: () => tenantSummary.totalWorkOrders },
      { key: 'users', label: t('dashboard.metrics.users'), resolve: () => tenantSummary.totalUsers },
      { key: 'storage', label: t('dashboard.metrics.storage'), resolve: () => tenantSummary.totalStorageBytes },
      { key: 'timestamp', label: t('dashboard.export.timestamp'), resolve: () => exportTimestamp },
    ], [{}]);
  };

  return (
    <div className="page-stack dashboard-page">
      <section className="dashboard-page__intro page-title-block">
        <h1 className="page-title-block__title">{t('dashboard.title')}</h1>
        <p className="page-title-block__description">{t('dashboard.description')}</p>
      </section>

      {environmentsQuery.isError ? (
        <article className="surface-card surface-card--soft">
          <p>{getApiErrorMessage(environmentsQuery.error, t('dashboard.messages.loadEnvironmentsError'))}</p>
        </article>
      ) : null}

      {tenantsQuery.isPending ? (
        <article className="surface-card surface-card--soft">
          <p>{t('dashboard.messages.loading')}</p>
        </article>
      ) : null}

      {tenantsQuery.isError ? (
        <article className="surface-card surface-card--soft">
          <p>{getApiErrorMessage(tenantsQuery.error, t('dashboard.messages.loadTenantsError'))}</p>
        </article>
      ) : null}

      {dashboardStatsQuery.isError && environmentScope === 'all' ? (
        <article className="surface-card surface-card--soft">
          <p>{getApiErrorMessage(dashboardStatsQuery.error, t('dashboard.messages.loadDashboardError'))}</p>
        </article>
      ) : null}

      <section className="dashboard-summary-grid">
        {summaryCards.map((metric) => {
          const Icon = metric.icon;

          return (
            <article key={metric.label} className="surface-card summary-card">
              <div className="summary-card__head">
                <div>
                  <p className="metric-label">{metric.label}</p>
                  <h2 className="metric-value">{metric.value}</h2>
                </div>
                <span className={`summary-card__icon summary-card__icon--${metric.tone}`}>
                  <Icon size={20} />
                </span>
              </div>
            </article>
          );
        })}
      </section>

      <article className="surface-card dashboard-overview-card">
        <div className="dashboard-overview-card__header">
          <div className="section-heading">
            <span className="section-heading__icon">
              <Building2 size={18} />
            </span>
            <div className="section-heading__copy">
              <p className="section-heading__title">{t('dashboard.table.title')}</p>
              <p className="section-copy">{t('dashboard.table.description', { count: visibleTenants.length, scope: selectedEnvironmentName })}</p>
            </div>
          </div>
          <div className="dashboard-overview-card__actions">
            <button type="button" className="button button--secondary dashboard-export-btn" onClick={handleExportMetrics}>
              <Download size={16} />
              {t('dashboard.actions.exportMetrics')}
            </button>
            <button type="button" className="button button--secondary dashboard-export-btn" onClick={handleExportTenants}>
              <Download size={16} />
              {t('dashboard.actions.exportTenants')}
            </button>
          </div>
        </div>

        <div className="dashboard-overview-card__filters">
          <label className="dashboard-search-field">
            <Search size={16} />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('dashboard.filters.searchPlaceholder')}
            />
          </label>
          <select
            className="field__control dashboard-overview-card__select dashboard-overview-card__select--status"
            aria-label={t('dashboard.filters.status')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
          >
            <option value="all">{t('dashboard.filters.allStatuses')}</option>
            <option value="active">{t('dashboard.status.active')}</option>
            <option value="inactive">{t('dashboard.status.inactive')}</option>
          </select>
        </div>

        {visibleTenants.length ? (
          <AppDataGrid
            rowData={visibleTenants}
            columnDefs={tenantColumnDefs}
            pageSize={12}
            height={560}
            className="app-data-grid--tenant-overview"
            onRowDoubleClicked={handleTenantRowDoubleClick}
            onCellContextMenu={handleTenantContextMenu}
            suppressBrowserContextMenu
          />
        ) : null}

        {!tenantsQuery.isPending && visibleTenants.length === 0 ? <p>{t('dashboard.messages.empty')}</p> : null}
      </article>

      {contextMenu ? (
        <div
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              openTenantDetails(contextMenu.tenant);
              setContextMenu(null);
            }}
          >
            {t('dashboard.contextMenu.openSameTab')}
          </button>
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              openTenantDetailsNewTab(contextMenu.tenant);
              setContextMenu(null);
            }}
          >
            {t('dashboard.contextMenu.openNewTab')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
